/* eslint-disable no-param-reassign */
import { THEME_SYNC_FILE_PREFIX } from 'constants/common.const';
import { createFrameDashedBorder, hexToRgba } from 'helpers/color.helper';
import { resourceUrl } from 'helpers/url.helper';
import { forEach, includes, split } from 'lodash';
import { BoothModel } from 'models/photo/booth.model';
import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { setBoothStateAction } from 'store/features/app/boothSlice';
import { setSelfeAppStateAction } from 'store/features/app/selfeAppSlice';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';

export const Context = React.createContext<ThemeContextReturnTypes>({});

interface ThemeContextProps {}
type ThemeContextReturnTypes = {};

const STYLE_ID = 'dynamic-app-style';

export const ThemeContext: FC<PropsWithChildren<ThemeContextProps>> = ({
  children,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { boothInfo } = useAppSelector((state) => state.booth);

  const handleAppendStyleToHead = (style: string) => {
    let styleElement = document.getElementById(
      STYLE_ID,
    ) as HTMLStyleElement | null;

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = STYLE_ID;
      document.head.appendChild(styleElement);
    }

    styleElement.innerHTML = style;
  };

  function handleSetRootBackgroundVariables(
    boothAttachment: BoothModel['UITemplateBoothFiles'],
  ) {
    const root = document.documentElement;

    forEach(boothAttachment, (attachmentData) => {
      root.style.setProperty(
        `${attachmentData?.FileKey}`,
        `url("${resourceUrl(attachmentData?.Url)}")`,
      );
    });
  }

  const handleLoadFont = async (
    boothAttachment: BoothModel['UITemplateBoothFiles'],
  ) => {
    forEach(boothAttachment, async (attachmentData) => {
      if (includes(attachmentData?.FileKey, THEME_SYNC_FILE_PREFIX.FONT)) {
        const partOfFileKey = split(
          attachmentData?.FileKey,
          THEME_SYNC_FILE_PREFIX.FONT_SEPARATE,
        );
        if (partOfFileKey?.[1]) {
          const font = new FontFace(
            partOfFileKey?.[1],
            `url("${resourceUrl(attachmentData?.Url)}")`,
            {
              ...(partOfFileKey?.[2] && { weight: partOfFileKey?.[2] }),
              ...(partOfFileKey?.[3] && { style: partOfFileKey?.[3] }),
            },
          );

          await font.load();
          document.fonts.add(font);
        }
      }
    });
  };

  const handleChangePhotoBoothStyle = useCallback(
    (boothData: BoothModel | null) => {
      // eslint-disable-next-line no-constant-condition
      if (boothData?.LogoImageUrl) {
        document.documentElement.style.setProperty(
          '--sync-primary-color',
          boothData?.PrimaryColor,
        );
        document.documentElement.style.setProperty(
          '--sync-secondary-color',
          boothData?.SecondaryColor,
        );
        document.documentElement.style.setProperty(
          '--sync-text-color',
          boothData?.TextColor,
        );
        document.documentElement.style.setProperty(
          '--sync-button-text-color',
          boothData?.ButtonTextColor,
        );
        document.documentElement.style.setProperty(
          '--sync-button-border-color',
          boothData?.ButtonBorderColor,
        );
        document.documentElement.style.setProperty(
          '--sync-input-background-color',
          boothData?.InputBackgroundColor,
        );
        document.documentElement.style.setProperty(
          '--sync-title-color',
          boothData?.TitleColor,
        );
        document.documentElement.style.setProperty(
          '--sync-border-title-1-color',
          boothData?.BorderTitleColor1,
        );
        document.documentElement.style.setProperty(
          '--sync-border-title-2-color',
          boothData?.BorderTitleColor2,
        );
        // Set background
        document.documentElement.style.setProperty(
          '--background-image-url',
          `url(${resourceUrl(boothData?.BackgroundImageUrl)})`,
        );
        // Set dashed border
        document.documentElement.style.setProperty(
          '--sync-live-view-dashed-border',
          `url("${createFrameDashedBorder(boothData?.TextColor)}")`,
        );
        document.documentElement.style.setProperty(
          '--sync-left-frame-dashed-border',
          `url("${createFrameDashedBorder(boothData?.PrimaryColor)}")`,
        );
        document.documentElement.style.setProperty(
          '--sync-right-frame-dashed-border',
          `url("${createFrameDashedBorder(boothData?.SecondaryColor)}")`,
        );
        document.documentElement.style.setProperty(
          '--sync-left-frame-background-color',
          hexToRgba(boothData?.PrimaryColor, 0.1),
        );
        document.documentElement.style.setProperty(
          '--sync-right-frame-background-color',
          hexToRgba(boothData?.SecondaryColor, 0.1),
        );
        handleAppendStyleToHead(boothData?.CustomCss);
        handleSetRootBackgroundVariables(boothData?.UITemplateBoothFiles);
        handleLoadFont(boothData?.UITemplateBoothFiles);
      }
    },
    [],
  );

  useEffect(() => {
    handleChangePhotoBoothStyle(boothInfo);
  }, [boothInfo, handleChangePhotoBoothStyle]);

  useEffect(() => {
    window.addEventListener('message', (event) => {
      if (
        !includes(event.origin, 'localhost') &&
        !includes(event.origin, 'funstudio') &&
        !includes(event.origin, 'photobooth-admin.vercel.app')
      )
        return;

      if (event.data.type === 'PREVIEW_CUSTOMIZE_UI') {
        handleChangePhotoBoothStyle(event.data?.payload?.appThemeData);
        if (event.data?.payload?.appState) {
          dispatch(setSelfeAppStateAction(event.data?.payload?.appState));
        }
        if (event.data?.payload?.boothState) {
          dispatch(setBoothStateAction(event.data?.payload?.boothState));
        }
      } else if (event.data.type === 'PREVIEW_REDIRECT') {
        navigate(event.data?.payload);
      }
    });

    return () => {
      window.removeEventListener('message', () => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Context.Provider value={useMemo(() => ({}), [])}>
      {children}
    </Context.Provider>
  );
};

export const useThemeContext = () => useContext(Context);
